<template>
  <v-sheet
    class="top-bar d-flex"
    height="58px"
  >
    <div class="ml-4 mt-4 w-25 d-flex" :style="{ height: '20px' }">
      <img
        src="@/assets/png/payscale-dark.png"
        alt="Job nav Logo"
      >
    </div>
    <div class="w-50 d-flex px-4">
      <div class="my-auto">
        <v-btn
          v-if="!isTemplate && !isHrisProfile"
          variant="outlined"
          rounded="lg"
          color="primary"
          size="small"
          :disabled="applyTemplateDisabled"
          @click="setModal({ field: 'applyJobProfileTemplate', value: true })"
        >
          Apply Template
        </v-btn>
        <FirstDraftLanguageTooltip
          v-if="authData.aiEnabled && !isHrisProfile && !isTemplate"
          class="ml-4"
          :show="isNonEnglish"
          :text="FIRST_DRAFT_PROFILE_TOOLTIP"
        >
          <FirstDraftButton
            :disabled="rescorePending || readOnly || isNonEnglish"
            variant="plain"
            class="pa-0"
            height="24"
            @click="handleIterateWithFirstDraft"
          >
            <span class="text-button-2">Iterate with First Draft</span>
          </FirstDraftButton>
        </FirstDraftLanguageTooltip>
      </div>
    </div>

    <div class="w-25 d-flex justify-end">
      <HistoryToggle
        class="pr-2 my-auto"
      />
      <CommentsToggle
        class="pr-2 my-auto"
      />
    </div>
    <div class="close-button-placeholder ml-4 pr-4 d-flex flex-column justify-center">
      <DpplAutoSizedButton
        class="ml-1 mb-1"
        :disabled="isRescoring"
        width="108"
        @click="onCloseClick"
      >
        Save & Close
      </DpplAutoSizedButton>
    </div>
  </v-sheet>

  <ApplyJobProfileTemplateModal @applied="handleTemplateApplied" />

  <Modal
    :show="show.createDraftFromHris"
    cancel-text="Cancel"
    action-text="Create Draft"
    :disabled="modalLoading"
    @close="setModal({ field: 'createDraftFromHris', value: false })"
    @action="handleCreateDraft"
  >
    <template #title>
      Create Draft
    </template>
    <template #content>
      A new draft job profile with the same content will be created.
    </template>
  </Modal>

  <Modal
    :show="showIterateModal"
    cancel-text="Cancel"
    action-text="Iterate"
    @close="showIterateModal = false"
    @action="handleIterateConfirm"
  >
    <template #title>
      Iterate with First Draft
    </template>
    <template #content>
      <p>Generating will overwrite the current content of this job profile.</p>
      <v-checkbox
        v-if="hasSummaryContent"
        v-model="useExistingSummary"
        label="Use existing summary content to generate new content"
        density="compact"
        hide-details
        class="mt-3"
      />
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { useRouter } from 'vue-router';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useAppLoading } from '@/stores/app/loading';
import { useAppRoute } from '@/stores/app/route';
import { useModals } from '@/stores/modals/modals';
import Modal from '@/components/shared/Modal.vue';
import CommentsToggle from '@/components/document/rightPanel/CommentsToggle.vue';
import HistoryToggle from '@/components/document/rightPanel/HistoryToggle.vue';
import FirstDraftButton from '@ui/components/firstDraft/FirstDraftButton.vue';
import FirstDraftLanguageTooltip from '@/components/firstDraft/FirstDraftLanguageTooltip.vue';
import { FIRST_DRAFT_PROFILE_TOOLTIP } from '@/helpers/language';
import DpplAutoSizedButton from '@/components/datapeople/DpplAutoSizedButton.vue';
import { useFirstDraft } from '@/stores/document/firstDraft';
import { useAppSideNav } from '@/stores/app/appSideNav';
import { isEmptyHtml } from '@ui/helpers/htmlToText';
import { closeExtensionWindow } from '@/helpers/extensionMessaging';
import { useTracking } from '@/stores/tracking/tracking';
import ApplyJobProfileTemplateModal from './ApplyJobProfileTemplateModal.vue';

const currentJobProfile = useCurrentJobProfile();
const {
  isTemplate, isHrisProfile, isRescoring, rescorePending, jobProfile, summaryField, summaryFieldId, readOnly,
  isNonEnglish,
} = storeToRefs(currentJobProfile);

const { authData } = storeToRefs(useAppAuthData());

const { isSideNavOpen } = storeToRefs(useAppSideNav());

const { route } = storeToRefs(useAppRoute());

const { modalLoading } = storeToRefs(useAppLoading());
const { show } = storeToRefs(useModals());
const { setModal } = useModals();
const router = useRouter();

const showIterateModal = ref(false);
const useExistingSummary = ref(true);

const hasSummaryContent = computed(() => {
  if (!summaryField.value?.html) return false;
  return !isEmptyHtml(summaryField.value.html);
});

const applyTemplateDisabled = computed(() => (
  rescorePending.value || readOnly.value
));

const handleIterateWithFirstDraft = () => {
  useExistingSummary.value = true;
  showIterateModal.value = true;
};

const handleIterateConfirm = async () => {
  const keepsSummary = hasSummaryContent.value && useExistingSummary.value;

  useTracking().firstDraftIterateClick({
    documentType: 'profile',
    useExistingSummary: keepsSummary ? 'true' : 'false',
  });

  showIterateModal.value = false;

  await useFirstDraft().generateAndApplyJobProfile({
    ulid: jobProfile.value?.ulid,
    useExistingSummary: keepsSummary,
    summaryFieldId: summaryFieldId.value,
  });
};

const handleCreateDraft = async () => {
  const sourceUlid = jobProfile.value?.ulid;
  const documentUlid = await currentJobProfile.createDraftFromHrisProfile();
  setModal({ field: 'createDraftFromHris', value: false });

  useTracking().jpEditorCreateDraftFromHris({
    ulid: sourceUlid,
    newUlid: documentUlid,
  });

  await router.push({ name: 'job-profile-view', params: { id: documentUlid } });
};

const handleTemplateApplied = async ({ documentUlid }) => {
  await currentJobProfile.loadJobProfile({ ulid: documentUlid });

  return router.replace({
    name: 'job-profile-view',
    params: { id: documentUlid },
    query: { ...(route.value?.query || {}) },
  });
};

const onCloseClick = () => {
  closeExtensionWindow();
};

onMounted(() => {
  isSideNavOpen.value = false;
});
</script>
